import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';

// Status polling interval in milliseconds
const DEFAULT_POLLING_INTERVAL = 3000; // 3 seconds

interface ProcessingDetails {
  status: string;
  progress?: number;
  processing_started_at?: string;
  processing_completed_at?: string;
  warnings?: string[];
  queue_position?: number;
}

interface VideoStatus {
  id: string;
  title: string;
  status: string;
  processed_url?: string;
  processing_details?: ProcessingDetails; 
  isProcessing: boolean;
  isComplete: boolean;
  hasFailed: boolean;
}

/**
 * Hook for polling video processing status
 * @param videoId The ID of the video to check
 * @param initialPolling Whether to start polling immediately
 * @param interval Polling interval in milliseconds
 */
export function useProcessingStatus(
  videoId: string | undefined,
  initialPolling = true,
  interval = DEFAULT_POLLING_INTERVAL
) {
  const [isPolling, setIsPolling] = useState<boolean>(initialPolling);
  const [status, setStatus] = useState<VideoStatus | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  // Function to check the status once
  const checkStatus = useCallback(async () => {
    if (!videoId) return;

    try {
      const response = await apiService.getVideoStatus(videoId);
      const videoData = response.data.data;

      const newStatus: VideoStatus = {
        id: videoData.id,
        title: videoData.title,
        status: videoData.status,
        processed_url: videoData.processed_url,
        processing_details: videoData.processing_details,
        isProcessing: videoData.status === 'Processing',
        isComplete: ['Preview Ready', 'Processed', 'Published'].includes(videoData.status),
        hasFailed: videoData.status === 'Processing Failed'
      };

      setStatus(newStatus);
      setLastChecked(new Date());
      setError(null);

      // If processing is complete or failed, stop polling
      if (newStatus.isComplete || newStatus.hasFailed) {
        setIsPolling(false);
      }

      return newStatus;
    } catch (err) {
      setError(err as Error);
      // Don't stop polling on error
      return null;
    }
  }, [videoId]);

  // Start/stop polling
  const startPolling = useCallback(() => {
    setIsPolling(true);
  }, []);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
  }, []);

  // Poll for status
  useEffect(() => {
    if (!videoId || !isPolling) return;

    // Check immediately on mount
    checkStatus();

    // Set up interval for polling
    const pollInterval = setInterval(() => {
      checkStatus();
    }, interval);

    // Clean up interval on unmount or when polling stops
    return () => {
      clearInterval(pollInterval);
    };
  }, [videoId, isPolling, interval, checkStatus]);

  return {
    status,
    error,
    isPolling,
    lastChecked,
    startPolling,
    stopPolling,
    checkStatus
  };
}

export default useProcessingStatus; 